import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import './datatable.scss';
import { dataTableAdapter } from './datatable.adapter';
import { useClickOutside } from '@/hooks/useClickOutside';
import Loader from '@/components/atoms/loader/loader';

type ColumnDef = {
    cell?: any;
    accessorKey: string;
    header: React.ReactNode;
};

type Props<T extends Record<string, any>> = {
    head: ColumnDef[];
    data: T[];
    loading?: boolean;
    onDelete?: (row: T) => void;
};

const SortAsc = () => (
    <svg className="c-datatable-main__sort-icon" viewBox="0 0 10 14" width="10" height="14">
        <path d="M5 0L10 6H0z" fill="currentColor" />
        <path d="M5 14L10 8H0z" fill="currentColor" opacity="0.3" />
    </svg>
);

const SortDesc = () => (
    <svg className="c-datatable-main__sort-icon" viewBox="0 0 10 14" width="10" height="14">
        <path d="M5 14L10 8H0z" fill="currentColor" />
        <path d="M5 0L10 6H0z" fill="currentColor" opacity="0.3" />
    </svg>
);

const SearchIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M5.9175 11.835C4.26363 11.835 2.86392 11.2622 1.71835 10.1166C0.572783 8.97108 0 7.57136 0 5.9175C0 4.26363 0.572783 2.86392 1.71835 1.71835C2.86392 0.572783 4.26363 0 5.9175 0C7.57136 0 8.97108 0.572783 10.1166 1.71835C11.2622 2.86392 11.835 4.26363 11.835 5.9175C11.835 6.58511 11.7288 7.21479 11.5164 7.80654C11.3039 8.39829 11.0156 8.92176 10.6515 9.37696L15.7496 14.4751C15.9165 14.642 16 14.8544 16 15.1124C16 15.3703 15.9165 15.5827 15.7496 15.7496C15.5827 15.9165 15.3703 16 15.1124 16C14.8544 16 14.642 15.9165 14.4751 15.7496L9.37696 10.6515C8.92176 11.0156 8.39829 11.3039 7.80654 11.5164C7.21479 11.7288 6.58511 11.835 5.9175 11.835ZM5.9175 10.0142C7.05548 10.0142 8.02276 9.61593 8.81935 8.81935C9.61593 8.02276 10.0142 7.05548 10.0142 5.9175C10.0142 4.77952 9.61593 3.81223 8.81935 3.01565C8.02276 2.21906 7.05548 1.82077 5.9175 1.82077C4.77952 1.82077 3.81223 2.21906 3.01565 3.01565C2.21906 3.81223 1.82077 4.77952 1.82077 5.9175C1.82077 7.05548 2.21906 8.02276 3.01565 8.81935C3.81223 9.61593 4.77952 10.0142 5.9175 10.0142Z" fill="#DB0061"/>
    </svg>

);

const SettingsIcon = () => (
    <svg width="24" height="20" viewBox="0 0 24 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9.1875 19.5C6.915 19.5 5.0625 17.6475 5.0625 15.375C5.0625 13.1025 6.915 11.25 9.1875 11.25C11.46 11.25 13.3125 13.1025 13.3125 15.375C13.3125 17.6475 11.46 19.5 9.1875 19.5ZM9.1875 12.75C7.74 12.75 6.5625 13.9275 6.5625 15.375C6.5625 16.8225 7.74 18 9.1875 18C10.635 18 11.8125 16.8225 11.8125 15.375C11.8125 13.9275 10.635 12.75 9.1875 12.75Z" fill="#DB0061"/>
        <path d="M14.8125 8.25C12.54 8.25 10.6875 6.3975 10.6875 4.125C10.6875 1.8525 12.54 0 14.8125 0C17.085 0 18.9375 1.8525 18.9375 4.125C18.9375 6.3975 17.085 8.25 14.8125 8.25ZM14.8125 1.5C13.365 1.5 12.1875 2.6775 12.1875 4.125C12.1875 5.5725 13.365 6.75 14.8125 6.75C16.26 6.75 17.4375 5.5725 17.4375 4.125C17.4375 2.6775 16.26 1.5 14.8125 1.5Z" fill="#DB0061"/>
        <path d="M23.25 16.08H15.375C14.9625 16.08 14.625 15.7425 14.625 15.33C14.625 14.9175 14.9625 14.58 15.375 14.58H23.25C23.6625 14.58 24 14.9175 24 15.33C24 15.7425 23.6625 16.08 23.25 16.08Z" fill="#DB0061"/>
        <path d="M8.625 4.82996H0.75C0.3375 4.82996 0 4.49246 0 4.07996C0 3.66746 0.3375 3.32996 0.75 3.32996H8.625C9.0375 3.32996 9.375 3.66746 9.375 4.07996C9.375 4.49246 9.0375 4.82996 8.625 4.82996Z" fill="#DB0061"/>
        <path d="M3 16.08H0.75C0.3375 16.08 0 15.7425 0 15.33C0 14.9175 0.3375 14.58 0.75 14.58H3C3.4125 14.58 3.75 14.9175 3.75 15.33C3.75 15.7425 3.4125 16.08 3 16.08Z" fill="#DB0061"/>
        <path d="M23.25 4.82996H21C20.5875 4.82996 20.25 4.49246 20.25 4.07996C20.25 3.66746 20.5875 3.32996 21 3.32996H23.25C23.6625 3.32996 24 3.66746 24 4.07996C24 4.49246 23.6625 4.82996 23.25 4.82996Z" fill="#DB0061"/>
    </svg>

);

const SortNone = () => (
    <svg className="c-datatable-main__sort-icon" viewBox="0 0 10 14" width="10" height="14">
        <path d="M5 0L10 6H0z" fill="currentColor" opacity="0.2" />
        <path d="M5 14L10 8H0z" fill="currentColor" opacity="0.2" />
    </svg>
);

const KebabIcon = () => (
    <svg viewBox="0 0 20 20" width="18" height="18" fill="currentColor">
        <circle cx="10" cy="4" r="2" />
        <circle cx="10" cy="10" r="2" />
        <circle cx="10" cy="16" r="2" />
    </svg>
);

export const DataTable = <T extends Record<string, any>>({
    data,
    head,
    loading = false,
    onDelete,
}: Props<T>) => {
    const [sorting, setSorting] = useState<
        { accessorKey: string; direction: 'asc' | 'desc' }[]
    >([]);
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 5,
    });
    const [search, setSearch] = useState('');
    const [searchOpen, setSearchOpen] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [menuOpen, setMenuOpen] = useState<number | null>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const closeMenu = useCallback(() => setMenuOpen(null), []);
    const menuRef = useClickOutside(closeMenu);

    const columns = useMemo(() => head, [head]);

    const { paginated, total, pageCount } = useMemo(
        () =>
            dataTableAdapter({
                data,
                search,
                sorting,
                pageIndex: pagination.pageIndex,
                pageSize: pagination.pageSize,
            }),
        [data, search, sorting, pagination]
    );

    const handleSort = (accessorKey: string) => {
        setSorting((prev) => {
            const current = prev.find((s) => s.accessorKey === accessorKey);
            if (!current) return [{ accessorKey, direction: 'asc' }];
            if (current.direction === 'asc')
                return [{ accessorKey, direction: 'desc' }];
            return [];
        });
        setPagination((p) => ({ ...p, pageIndex: 0 }));
        setSettingsOpen(false);
    };

    const closeSettings = useCallback(() => setSettingsOpen(false), []);
    const settingsRef = useClickOutside(closeSettings);

    const from =
        total === 0 ? 0 : pagination.pageIndex * pagination.pageSize + 1;
    const to = Math.min(
        (pagination.pageIndex + 1) * pagination.pageSize,
        total
    );

    const getPageNumbers = () => {
        const pages: number[] = [];
        const maxVisible = 5;
        let start = Math.max(
            0,
            pagination.pageIndex - Math.floor(maxVisible / 2)
        );
        const end = Math.min(pageCount, start + maxVisible);
        if (end - start < maxVisible) {
            start = Math.max(0, end - maxVisible);
        }
        for (let i = start; i < end; i++) pages.push(i);
        return pages;
    };

    useEffect(() => {
        if (searchOpen && searchRef.current) searchRef.current.focus();
    }, [searchOpen]);

    useEffect(() => {
        setPagination((p) => ({ ...p, pageIndex: 0 }));
    }, [data.length]);

    const getSortState = (key: string) => {
        const s = sorting.find((x) => x.accessorKey === key);
        return s ? s.direction : 'none';
    };

    return (
        <div className="c-datatable-main">
            <div className="c-datatable-main__toolbar">
                <div className="c-datatable-main__toolbar-left">
                    <button
                        className={`c-datatable-main__toolbar-btn${searchOpen ? ' c-datatable-main__toolbar-btn--active' : ''}`}
                        onClick={() => setSearchOpen((p) => !p)}
                        aria-label="Buscar"
                    >
                        <SearchIcon />
                    </button>

                    <div
                        className={`c-datatable-main__search-slide${searchOpen ? ' c-datatable-main__search-slide--open' : ''}`}
                    >
                        <input
                            ref={searchRef}
                            type="text"
                            placeholder="Buscar..."
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPagination((p) => ({ ...p, pageIndex: 0 }));
                            }}
                            className="c-datatable-main__search-input"
                        />
                        {search && (
                            <button
                                className="c-datatable-main__search-clear"
                                onClick={() => {
                                    setSearch('');
                                    setPagination((p) => ({
                                        ...p,
                                        pageIndex: 0,
                                    }));
                                }}
                                aria-label="Limpiar búsqueda"
                            >
                                &times;
                            </button>
                        )}
                    </div>

                    <div ref={settingsRef} className="c-datatable-main__settings-wrapper">
                        <button
                            className={`c-datatable-main__toolbar-btn${settingsOpen ? ' c-datatable-main__toolbar-btn--active' : ''}`}
                            onClick={() => setSettingsOpen((p) => !p)}
                            aria-label="Configuración"
                        >
                            <SettingsIcon />
                        </button>

                        {settingsOpen && (
                            <div className="c-datatable-main__dropdown">
                                <div className="c-datatable-main__dropdown-header">
                                    Ordenar por
                                </div>
                                {columns
                                    .filter((c) => c.accessorKey !== 'select')
                                    .map((col) => {
                                        const state = getSortState(
                                            col.accessorKey
                                        );
                                        return (
                                            <button
                                                key={col.accessorKey}
                                                className="c-datatable-main__dropdown-item"
                                                onClick={() =>
                                                    handleSort(
                                                        col.accessorKey
                                                    )
                                                }
                                            >
                                                <span>
                                                    {typeof col.header ===
                                                    'string'
                                                        ? col.header
                                                        : col.accessorKey}
                                                </span>
                                                <span className="c-datatable-main__dropdown-sort">
                                                    {state === 'asc' && (
                                                        <SortAsc />
                                                    )}
                                                    {state === 'desc' && (
                                                        <SortDesc />
                                                    )}
                                                    {state === 'none' && (
                                                        <SortNone />
                                                    )}
                                                </span>
                                            </button>
                                        );
                                    })}
                            </div>
                        )}
                    </div>
                </div>

            </div>

            {loading ? (
                <div className="c-datatable-main__loading">
                    <Loader size="__medium" />
                </div>
            ) : paginated.length === 0 ? (
                <div className="c-datatable-main__empty">
                    No se encontraron resultados
                </div>
            ) : (
                <>
                    <div className="c-datatable-main__table-wrapper">
                        <table className="c-datatable-main--table">
                            <thead className="c-datatable-main--header">
                                <tr>
                                    {columns.map((col) => {
                                        const sort = sorting.find(
                                            (s) =>
                                                s.accessorKey ===
                                                col.accessorKey
                                        );
                                        const isSelectCol = col.accessorKey === 'select';
                                        return (
                                            <th
                                                key={col.accessorKey}
                                                onClick={isSelectCol ? undefined : () => handleSort(col.accessorKey)}
                                                className={
                                                    isSelectCol
                                                        ? 'c-datatable-main--header-th c-datatable-main--header-th--action'
                                                        : sort
                                                        ? 'c-datatable-main--header-th c-datatable-main--header-th--active'
                                                        : 'c-datatable-main--header-th'
                                                }
                                            >
                                                <span className="c-datatable-main--header-th-content">
                                                    {col.header}
                                                    {!isSelectCol && (sort ? (
                                                        sort.direction ===
                                                        'asc' ? (
                                                            <SortAsc />
                                                        ) : (
                                                            <SortDesc />
                                                        )
                                                    ) : (
                                                        <SortNone />
                                                    ))}
                                                </span>
                                            </th>
                                        );
                                    })}
                                    {onDelete && <th className="c-datatable-main--header-th c-datatable-main--header-th--action" />}
                                </tr>
                            </thead>

                            <tbody className="c-datatable-main--body">
                                {paginated.map((row, idx) => (
                                    <tr key={row.id || idx}>
                                        {columns.map((col) => (
                                            <td key={col.accessorKey}>
                                                {typeof col.cell ===
                                                'function'
                                                    ? col.cell({
                                                          row,
                                                          index: idx,
                                                      })
                                                    : row[col.accessorKey]}
                                            </td>
                                        ))}
                                        {onDelete && (
                                            <td className="c-datatable-main__kebab-cell">
                                                <div
                                                    ref={menuOpen === idx ? menuRef : undefined}
                                                    className="c-datatable-main__kebab-wrapper"
                                                >
                                                    <button
                                                        className={`c-datatable-main__kebab-btn${menuOpen === idx ? ' c-datatable-main__kebab-btn--open' : ''}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setMenuOpen(menuOpen === idx ? null : idx);
                                                        }}
                                                        aria-label="Acciones"
                                                    >
                                                        <KebabIcon />
                                                    </button>
                                                    {menuOpen === idx && (
                                                        <div className="c-datatable-main__kebab-menu">
                                                            <button
                                                                className="c-datatable-main__kebab-menu-item"
                                                                onClick={() => {
                                                                    onDelete(row);
                                                                    setMenuOpen(null);
                                                                }}
                                                            >
                                                                Eliminar
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="c-datatable-main__footer">
                        <div className="c-datatable-main__page-size-wrapper">
                            <span className="c-datatable-main__page-size-label">
                                Ítems por página:
                            </span>
                            <select
                                value={pagination.pageSize}
                                onChange={(e) =>
                                    setPagination(() => ({
                                        pageIndex: 0,
                                        pageSize: Number(e.target.value),
                                    }))
                                }
                                className="c-datatable-main__page-size"
                            >
                                {[5, 10, 20, 50].map((size) => (
                                    <option key={size} value={size}>
                                        {size}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <span className="c-datatable-main__results-info">
                            {from}-{to} de <strong>{total}</strong> ítems
                        </span>

                        <div className="c-datatable-main--pagination">
                            <button
                                className="c-datatable-main--pagination-btn c-datatable-main--pagination-btn--text"
                                onClick={() =>
                                    setPagination((p) => ({
                                        ...p,
                                        pageIndex: Math.max(0, p.pageIndex - 1),
                                    }))
                                }
                                disabled={pagination.pageIndex === 0}
                                aria-label="Página anterior"
                            >
                                {'< Atrás'}
                            </button>

                            {getPageNumbers().map((page) => (
                                <button
                                    key={page}
                                    className={`c-datatable-main--pagination-btn${
                                        page === pagination.pageIndex
                                            ? ' c-datatable-main--pagination-btn--active'
                                            : ''
                                    }`}
                                    onClick={() =>
                                        setPagination((p) => ({
                                            ...p,
                                            pageIndex: page,
                                        }))
                                    }
                                >
                                    {page + 1}
                                </button>
                            ))}

                            <button
                                className="c-datatable-main--pagination-btn c-datatable-main--pagination-btn--text"
                                onClick={() =>
                                    setPagination((p) => ({
                                        ...p,
                                        pageIndex: Math.min(
                                            pageCount - 1,
                                            p.pageIndex + 1
                                        ),
                                    }))
                                }
                                disabled={pagination.pageIndex + 1 >= pageCount}
                                aria-label="Página siguiente"
                            >
                                {'Siguiente >'}
                            </button>
                        </div>
                    </div>
                </>
            )}

        </div>
    );
};
